import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment/moment';
import { Icon } from '@iconify/react/dist/iconify.js';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import Loader from '../../../../components/Looder';
import { notify } from '../../../../utils/notification';
import DeleteConfirmModal from './DeleteConfirmModal';
import SmtpConfigFormModal from './SmtpConfigFormModal';
import SmtpConfigViewModal from './SmtpConfigViewModal';
import { usePageRefresh } from '../../../../context/PageRefreshContext';
import {
    listSmtpConfigsReq,
    updateSmtpConfigReq,
    deleteSmtpConfigReq,
    smtpConfigErrorMessage,
} from '../../../../axios/request/SocietyController/CtrlSmtpConfigReqs/CtrlSmtpConfigReqs';

const iconBtn = 'tw-p-1 tw-rounded tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed';

/**
 * Outgoing mail (SMTP) accounts for one society — /api/society-smtp-configs.
 * At most one account is the default; the backend clears the old default itself,
 * so default changes reload the list rather than patching rows locally.
 */
const SmtpConfigLayer = ({ societyId }) => {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [pendingIds, setPendingIds] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback((silent = false) => {
        if (societyId == null) return undefined;
        let cancelled = false;
        if (!silent) setLoading(true);
        setLoadError('');
        listSmtpConfigsReq(societyId)
            .then((res) => {
                if (cancelled) return;
                if (res?.success === false) throw new Error(res?.message);
                setConfigs(Array.isArray(res?.data) ? res.data : []);
            })
            .catch((err) => { if (!cancelled) setLoadError(smtpConfigErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [societyId]);

    useEffect(() => load(), [load]);
    usePageRefresh(load);

    const withPending = async (uuid, fn) => {
        setPendingIds((p) => [...p, uuid]);
        try { await fn(); } finally { setPendingIds((p) => p.filter((x) => x !== uuid)); }
    };

    const update = (cfg, body, successMsg, reload = false, warning = null) => withPending(cfg.uuid, async () => {
        try {
            const res = await updateSmtpConfigReq(societyId, cfg.uuid, body);
            if (res?.success === false) throw new Error(res?.message);
            notify(warning ? 'warning' : 'success', warning || successMsg);
            if (reload) load(true);
            else setConfigs((p) => p.map((c) => (c.uuid === cfg.uuid ? (res?.data || { ...c, ...body }) : c)));
        } catch (err) {
            notify('error', smtpConfigErrorMessage(err));
        }
    });

    const makeDefault = (cfg) => update(cfg, { is_default: 1 }, `"${cfg.config_name}" is now the default account`, true);

    const toggleActive = (cfg, on) => {
        // The backend keeps the default flag on an inactive account — nudge the admin to pick another.
        const nudge = !on && cfg.is_default === 1 && configs.some((c) => c.uuid !== cfg.uuid && c.is_active === 1);
        update(cfg, { is_active: on ? 1 : 0 }, on ? 'Account activated' : 'Account deactivated', false,
            nudge ? 'Default account deactivated — make an active account the default.' : null);
    };

    const handleDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            const res = await deleteSmtpConfigReq(societyId, toDelete.uuid);
            if (res?.success === false) throw new Error(res?.message);
            notify('success', res?.message || 'Email account deleted');
            if (toDelete.is_default === 1) load(true);
            else setConfigs((p) => p.filter((c) => c.uuid !== toDelete.uuid));
            setToDelete(null);
        } catch (err) {
            notify('error', smtpConfigErrorMessage(err));
        } finally {
            setDeleting(false);
        }
    };

    const openForm = (cfg = null) => { setEditing(cfg); setFormOpen(true); };

    const handleSaved = (saved, { isEdit, defaultChanged }) => {
        setFormOpen(false);
        // A new default also flips the previous one, which the response doesn't carry.
        if (!saved || defaultChanged || !isEdit) { load(true); return; }
        setConfigs((p) => p.map((c) => (c.uuid === saved.uuid ? saved : c)));
    };

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                    <span className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-sky-50 tw-text-sky-500 tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Icon icon="solar:letter-linear" className="tw-text-xl" />
                    </span>
                    <div className="tw-min-w-0">
                        <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Email Accounts (SMTP)</div>
                        <div className="tw-text-xs tw-text-gray-500">
                            Outgoing mail server for this community. One account is allowed.
                        </div>
                    </div>
                </div>
                {/* One account per community — edit or delete the existing one instead. */}
                <button onClick={() => openForm()} disabled={loading || !!loadError || configs.length > 0}
                    title={configs.length > 0 ? 'Only one email account is allowed per community' : undefined}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                    <Icon icon="mdi:plus" className="tw-text-sm" />
                    Add Account
                </button>
            </div>

            {/* Body */}
            <div className="tw-p-5">
                {loading ? (
                    <div className="tw-py-16 tw-flex tw-justify-center"><Loader /></div>
                ) : loadError ? (
                    <div className="tw-py-14 tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <Icon icon="mdi:alert-circle-outline" className="tw-text-4xl tw-text-red-300" />
                        <p className="tw-text-xs tw-text-red-500 tw-text-center tw-mb-1">{loadError}</p>
                        <button onClick={() => load()}
                            className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                            <Icon icon="mdi:refresh" className="tw-text-sm" /> Retry
                        </button>
                    </div>
                ) : configs.length === 0 ? (
                    <div className="tw-py-12 tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <Icon icon="mdi:email-off-outline" className="tw-text-3xl tw-text-gray-300" />
                        <p className="tw-text-xs tw-text-gray-400 tw-mb-0">No email accounts yet</p>
                    </div>
                ) : (
                    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                    {['Name', 'From', 'Server', 'Status', 'Actions'].map((h) => (
                                        <th key={h} className={`tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px] tw-whitespace-nowrap ${h === 'Actions' ? 'tw-text-center' : 'tw-text-left'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="tw-divide-y tw-divide-gray-50">
                                {configs.map((c) => {
                                    const pending = pendingIds.includes(c.uuid);
                                    const isDefault = c.is_default === 1;
                                    return (
                                        <tr key={c.uuid} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-text-gray-800 tw-whitespace-nowrap">
                                                    {c.config_name}
                                                    {isDefault && (
                                                        <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-50 tw-text-amber-700 tw-text-[10px] tw-font-semibold">
                                                            <Icon icon="mdi:star" className="tw-text-[11px]" /> Default
                                                        </span>
                                                    )}
                                                </div>
                                                {c.last_error ? (
                                                    <div className="tw-text-[11px] tw-text-red-500 tw-mt-0.5" title={c.last_error}>{c.last_error}</div>
                                                ) : c.last_verified_at && (
                                                    <div className="tw-text-[11px] tw-text-emerald-600 tw-mt-0.5">Verified {moment(c.last_verified_at).fromNow()}</div>
                                                )}
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-text-gray-800">{c.from_email}</div>
                                                {c.from_name && <div className="tw-text-[11px] tw-text-gray-500">{c.from_name}</div>}
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-whitespace-nowrap">
                                                <div className="tw-text-gray-800">{c.host}:{c.port}</div>
                                                <div className="tw-text-[11px] tw-text-gray-500">{c.encryption}</div>
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <ToggleSwitch size="sm" id={`smtp-active-${c.uuid}`}
                                                        checked={c.is_active === 1} disabled={pending}
                                                        onChange={(on) => toggleActive(c, on)} />
                                                    <span className={`tw-text-[11px] ${c.is_active === 1 ? 'tw-text-emerald-600' : 'tw-text-gray-400'}`}>
                                                        {c.is_active === 1 ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                                    <button onClick={() => makeDefault(c)} disabled={pending || isDefault}
                                                        className={`${iconBtn} tw-bg-amber-50 tw-text-amber-600 hover:tw-bg-amber-100`}
                                                        title={isDefault ? 'Default account' : 'Make default'}>
                                                        <Icon icon={isDefault ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-sm" />
                                                    </button>
                                                    <button onClick={() => setViewing(c)}
                                                        className={`${iconBtn} tw-bg-slate-50 tw-text-slate-600 hover:tw-bg-slate-100`} title="View">
                                                        <Icon icon="iconamoon:eye-light" className="tw-text-sm" />
                                                    </button>
                                                    <button onClick={() => openForm(c)} disabled={pending}
                                                        className={`${iconBtn} tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100`} title="Edit">
                                                        <Icon icon="lucide:edit" className="tw-text-sm" />
                                                    </button>
                                                    <button onClick={() => setToDelete(c)} disabled={pending}
                                                        className={`${iconBtn} tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100`} title="Delete">
                                                        <Icon icon="mingcute:delete-2-line" className="tw-text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <SmtpConfigViewModal open={!!viewing} societyId={societyId} config={viewing}
                onClose={() => setViewing(null)}
                onEdit={() => { const c = viewing; setViewing(null); openForm(c); }} />

            <SmtpConfigFormModal open={formOpen} societyId={societyId} config={editing}
                onClose={() => setFormOpen(false)} onSaved={handleSaved} />

            <DeleteConfirmModal open={!!toDelete} title="Delete Email Account"
                itemName={toDelete?.config_name} isLoading={deleting}
                description="This permanently removes the account and its saved password."
                handleClose={() => setToDelete(null)} handleConfirm={handleDelete} />
        </div>
    );
};

export default SmtpConfigLayer;
